import {
  fieldAddProductVulcan,
  fieldProductVulcan,
  fieldProvinceVUL,
  fieldCityVul,
  showProductABBC,
  prequalificationField
} from '../../../utils/constant';
import { filterProductsByProvince } from '../../vulcanHelpers/customerSection/utils';

/**
 * Pure rewrite of the PRODUCT-FILTER parts of `CustomerHelpers`
 * (vulcanHelpers/customerSection/customerHelpers.js) — the `productVulcan` and
 * `addProduct` option filtering, the Vulcan default addProduct value, and the
 * prequalification resets. (The two hide branches — isVulcanOrder /
 * newWirelineService — are in vulcanFieldRules.js's `customerSectionHides`.)
 *
 * Faithful to the original, including two provably-dead paths kept out here:
 *  - the CORP_STORE `businessConnect` filter set `fields.options`, but BOTH the
 *    Vulcan and WLN branches immediately overwrite `fields.options` by re-reading
 *    `formFields[addProduct].options` (which that step never touched), so it never
 *    reaches render (and WLN removes businessConnect anyway);
 *  - `isOutletAllowedForTelusInternetBackup` now always returns true, so the
 *    outlet-based Internet-Backup removal never fires.
 *
 * `getFieldValue`-gated reads (serviceAddress/serviceCity/productVulcan) become
 * raw `values[...]` reads (the hidden-driving-field nuance in memory
 * [[forms-core-fieldrules-pure-rewrite]]). `handleVulcanProductDisable`'s in-place
 * option mutation becomes a fresh-object map. Verified by
 * rules/customerProduct.diff.test.js.
 *
 * @param {{orderData: Object, channelName?: string, dealershipName?: string, salesRepOutlet?: string, locale: string, t: Object}} ctx
 * @returns {import('../vulcanFieldRules').VulcanRule}
 */
export function makeCustomerProductRule(ctx) {
  const { orderData, dealershipName, locale, t } = ctx;
  const {
    vulFibreInternet,
    vulBusinessWifi,
    vulBusinessConnect,
    vulWhsia,
    vulMobility,
    vulInternetBackup,
    vulBusinessOptikTV,
    vulOfficePhone,
    vulCopperInternet,
    vulFibreOpticDrop,
    businessWifiVul
  } = t;

  const wlnProducts = [
    vulBusinessOptikTV,
    vulOfficePhone,
    vulWhsia,
    vulCopperInternet,
    vulInternetBackup,
    vulFibreOpticDrop
  ];
  const productForABBC = [
    vulInternetBackup,
    vulBusinessOptikTV,
    vulOfficePhone
  ];

  // WOW! mobile boutique dealership: only Fibre / Business Wifi / Mobility.
  const dealershipAllowed = () => {
    if (!dealershipName) return null;
    const rules = {
      'wow! mobile boutique': [vulFibreInternet, vulBusinessWifi, vulMobility]
    };
    return rules[dealershipName.toLowerCase()] || null;
  };

  // handleVulcanProductDisable, as a fresh-object option map.
  const applyProductDisable = (options, productVulcanValue, isVulcan) => {
    const hasBusinessWifiExisting =
      Array.isArray(productVulcanValue) &&
      productVulcanValue.includes(businessWifiVul);
    return options.map(o => {
      const v = o.fields.value;
      if (v !== vulFibreInternet && v !== vulBusinessWifi) return o;
      let disabled;
      if (!isVulcan) disabled = false;
      else if (v === vulFibreInternet) disabled = true;
      else disabled = !hasBusinessWifiExisting; // BusinessWifi
      return { ...o, fields: { ...o.fields, disabledOption: disabled } };
    });
  };

  const isFibreOpticDropEligible = (serviceAddress, serviceCity) => {
    const city = (serviceCity || '').trim().toLowerCase();
    const prov = (serviceAddress || '').trim().toLowerCase();
    const ontarioCities = [
      'london',
      'kitchener',
      'cambridge',
      'st. catharines',
      'st catharines',
      'saint catharines'
    ];
    const quebecCities = ['laval'];
    const quebecAliases = ['quebec', 'québec', 'que.', 'qc'];
    return (
      (prov === 'ontario' && ontarioCities.includes(city)) ||
      (quebecAliases.includes(prov) && quebecCities.includes(city))
    );
  };

  return (field, state, values) => {
    const { name } = field;
    const isVulcan = orderData.isThisVulcanOrder;
    const options = state.options || field.options || [];
    const serviceAddress = values[fieldProvinceVUL] || orderData.province_VUL;

    // productVulcan: drop WLN products for Vulcan, drop wHSIA for WLN.
    if (name === fieldProductVulcan) {
      if (isVulcan) {
        return {
          options: options.filter(o => !wlnProducts.includes(o.fields.value))
        };
      }
      return { options: options.filter(o => o.fields.value !== vulWhsia) };
    }

    if (name === fieldAddProductVulcan) {
      const patch = {};

      // Vulcan default value when none chosen yet (the setResetSection side
      // effect is an app concern, handled at Stage 3 wiring).
      if (!values[fieldAddProductVulcan] && isVulcan) {
        patch.value = [vulFibreInternet, vulBusinessWifi];
      }

      // Disable Fibre/BusinessWifi per productVulcan + flow.
      let opts = applyProductDisable(
        options,
        values[fieldProductVulcan],
        isVulcan
      );

      if (isVulcan) {
        opts = opts.filter(o => !wlnProducts.includes(o.fields.value));
        const allowed = dealershipAllowed();
        if (allowed) opts = opts.filter(o => allowed.includes(o.fields.value));
      } else {
        patch.note = '';
        const isABBC = showProductABBC.includes(serviceAddress);
        if (!isABBC)
          opts = opts.filter(o => !productForABBC.includes(o.fields.value));
        const serviceCity = values[fieldCityVul] || orderData.city_VUL;
        if (!isFibreOpticDropEligible(serviceAddress, serviceCity)) {
          opts = opts.filter(o => o.fields.value !== vulFibreOpticDrop);
        }
        opts = opts.filter(
          o =>
            ![vulWhsia, vulMobility, vulBusinessConnect].includes(
              o.fields.value
            )
        );
      }

      // Province filter (Eastern provinces → Business Wifi + TSB only).
      opts = filterProductsByProvince(serviceAddress, opts, locale);
      patch.options = opts;
      return patch;
    }

    return undefined;
  };
}

/**
 * Prequalification reset: clears the TSB prequalification fields + isHomeBased
 * when addProduct has a value that does NOT include TSB (was a cross-field wipe
 * from the addProduct handler; here each target field derives its own reset).
 *
 * @param {{t: Object}} ctx
 * @returns {import('../vulcanFieldRules').VulcanRule}
 */
export function makePrequalificationResetRule(ctx) {
  const { t } = ctx;
  const resetFields = new Set([...prequalificationField, 'isHomeBased']);
  return (field, state, values) => {
    if (!resetFields.has(field.name)) return undefined;
    const addProduct = values[fieldAddProductVulcan];
    if (addProduct && !addProduct.includes(t.vulTSB)) {
      return { value: undefined };
    }
    return undefined;
  };
}

export default makeCustomerProductRule;
